import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import cn from 'classnames';
import PolicyTableStore, { PRIORITY_BUTTONS } from '../../stores/Policy/PolicyTableStore';
import { ModalMode } from '../../stores/Policy/PolicyModalStore';
import SnackStore from '../../stores/SnackStore';
import { DEBUG_FLAG } from '../../debug';
import { Card } from '../Fuse';
import Table from '../Fuse/Table';
import Icon from '../Icon/Icon';
import FuseButton from '../FuseControls/FuseButton';
import FuseMenu from '../FuseControls/FuseMenu';
import PfptLabelWithInfo from '../PfptLabelWithInfo/PfptLabelWithInfo';
import { TextWithTitle } from '../ConsoleFragments';
import './PolicyTable.css';

const block = b.lock('PolicyTable');

class PolicyTable extends Component {
    static propTypes = {
        store: PolicyTableStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
        searchPlaceholder: PropTypes.string,
        itemName: PropTypes.string.isRequired,
        itemNameFull: PropTypes.string.isRequired,
        defaultItemName: PropTypes.string,
        defaultItemInfo: PropTypes.string,
        changePriorityAction: PropTypes.bool,
        loadFailedMessage: PropTypes.string.isRequired,
        className: PropTypes.string,
        priorityMode: PropTypes.bool,
        subHeader: PropTypes.node,
        noItemsText: PropTypes.string,
        columnsConfig: PropTypes.shape({
            colFilter: PropTypes.func,
            extraColumns: PropTypes.arrayOf(PropTypes.shape({
                name: PropTypes.string.isRequired,
                title: PropTypes.node.isRequired,
                render: PropTypes.func.isRequired,
                index: PropTypes.number.isRequired,
            }))
        }),
        searchFilter: PropTypes.func,
        renderName: PropTypes.func,
        sortFn: PropTypes.func,
        readOnly: PropTypes.bool,
        ...Table.propTypes,
    };

    static COL_INDEXES = {
        RAW: 0,
        PRIORITY: 10,
        NAME: 20,
        DESCRIPTION: 30,
        STATUS: 40,
        MENU: 50,
    }

    columns = [
        ...DEBUG_FLAG ? [{
            name: 'json',
            title: <div style={{ width: 700 }}>RAW</div>,
            render: (item) => <pre style={{ width: 700 }}>{JSON.stringify(item, null, 2)}</pre>,
            index: PolicyTable.COL_INDEXES.RAW,
        }] : [],
        {
            name: 'priority',
            title: 'Priority',
            render: (item) => item.fixedPriority,
            index: PolicyTable.COL_INDEXES.PRIORITY
        },
        {
            name: 'name',
            title: 'Name',
            render: (item) => this.renderName(item),
            index: PolicyTable.COL_INDEXES.NAME
        },
        {
            name: 'description',
            title: 'Description',
            render: (item) => <TextWithTitle
                text={item.description && item.description.trim() || '—'}
                className={block('CellContent', { ellipsis: true })}
            />,
            index: PolicyTable.COL_INDEXES.DESCRIPTION
        },
        {
            name: 'status',
            title: 'Status',
            render: (item) => <span className={block('CellContent', { disabled: item.status === 'DISABLED' })}>
                {item.status[0] + item.status.slice(1).toLowerCase()}
            </span>,
            index: PolicyTable.COL_INDEXES.STATUS
        },
        {
            name: 'menu',
            title: '',
            render: (item, index) => <FuseMenu
                qa={`policy-menu-${index}`}
                open={this.props.store.menu.id === item.id}
                toggle={() => this.props.store.menu.toggle(item.id)}
                items={[
                    {
                        label: `Edit ${this.props.itemName}…`,
                        icon: 'edit',
                        qa: 'menu-edit',
                        onClick: () => this.props.store.openModal(ModalMode.EDIT, item),
                    },
                    {
                        label: `Duplicate ${this.props.itemName}`,
                        icon: 'copy',
                        qa: 'menu-copy',
                        disabled: !!item.isDefault,
                        onClick: () => this.makeCopy(item),
                    },
                    {
                        label: `Delete ${this.props.itemName}…`,
                        icon: 'delete',
                        qa: 'menu-delete',
                        disabled: !!item.isDefault,
                        onClick: () => this.props.store.openModal(ModalMode.DELETE, item),
                    },
                ]}
            />,
            index: PolicyTable.COL_INDEXES.MENU
        },
    ];

    priorityTableColumns = [
        {
            name: 'priority',
            title: 'Priority',
            render: (item, itemIndex) => (!item.isDefault ? itemIndex + 1 :
                <Icon className={block('LockIcon')} type={'lock_outline'} />),
        },
        {
            name: 'name',
            title: 'Name',
            render: (item) => this.renderName(item),
        },
        {
            name: 'reorder',
            title: 'Change Order',
            render: (item, itemIndex) => {
                const { store } = this.props;

                return !item.isDefault ? <Fragment>
                    <FuseButton
                        className={block('OrderButton')}
                        disabled={!store.canMoveUp(itemIndex)}
                        icon="up"
                        qa="order-up"
                        onClick={store.getOnPriorityButtonClickFn(itemIndex, itemIndex - 1, PRIORITY_BUTTONS.UP)}
                        focus={store.isPriorityButtonFocused(itemIndex, PRIORITY_BUTTONS.UP)}
                        onBlur={store.getOnPriorityButtonBlurFn(itemIndex, PRIORITY_BUTTONS.UP)}
                    />
                    <FuseButton
                        className={block('OrderButton')}
                        disabled={!store.canMoveDown(itemIndex)}
                        icon="down"
                        qa="order-down"
                        onClick={store.getOnPriorityButtonClickFn(itemIndex, itemIndex + 1, PRIORITY_BUTTONS.DOWN)}
                        focus={store.isPriorityButtonFocused(itemIndex, PRIORITY_BUTTONS.DOWN)}
                        onBlur={store.getOnPriorityButtonBlurFn(itemIndex, PRIORITY_BUTTONS.DOWN)}
                    />
                </Fragment> : null;
            },
        },
        {
            name: 'orderTop',
            title: 'Top',
            render: (item, itemIndex) => {
                if (item.isDefault) {
                    return;
                }
                const { store } = this.props;
                const firstCanMoveItemIndex = store.getFirstCanMoveItem();
                return <FuseButton
                    className={block('OrderButton')}
                    disabled={itemIndex <= firstCanMoveItemIndex || firstCanMoveItemIndex === -1}
                    icon="top"
                    onClick={store.getOnPriorityButtonClickFn(itemIndex, firstCanMoveItemIndex, PRIORITY_BUTTONS.TOP)}
                    focus={store.isPriorityButtonFocused(itemIndex, PRIORITY_BUTTONS.TOP)}
                    onBlur={store.getOnPriorityButtonBlurFn(itemIndex, PRIORITY_BUTTONS.TOP)}
                    qa={`item-name-${item.name} order-top`}
                />;
            },
        },
        {
            name: 'orderBottom',
            title: 'Bottom',
            render: (item, itemIndex) => {
                if (item.isDefault) {
                    return;
                }
                const { store } = this.props;
                const lastCanMoveItemIndex = store.getLastCanMoveItem();
                return <FuseButton
                    className={block('OrderButton')}
                    disabled={itemIndex >= lastCanMoveItemIndex || lastCanMoveItemIndex === -1}
                    icon="bottom"
                    onClick={store.getOnPriorityButtonClickFn(itemIndex, lastCanMoveItemIndex, PRIORITY_BUTTONS.BOTTOM)}
                    focus={store.isPriorityButtonFocused(itemIndex, PRIORITY_BUTTONS.BOTTOM)}
                    onBlur={store.getOnPriorityButtonBlurFn(itemIndex, PRIORITY_BUTTONS.BOTTOM)}
                    qa={`item-name-${item.name} order-bottom`}
                />;
            },
        },
    ];

    componentWillMount() {
        this.props.store.search.setValue('');
        this.props.store.items.clear();
        this.loadItems(this.props);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.store !== this.props.store && nextProps.store.items.length === 0) {
            this.loadItems(nextProps);
        }
    }

    loadItems = (props) => {
        props.store.load().catch((error) => props.snack.showError(`${props.loadFailedMessage} ${error.message}`));
    };

    makeCopy = (item) => {
        const { snack, itemName, itemNameFull } = this.props;
        const name = itemNameFull || itemName;

        const isDisabled = itemName.toLowerCase() !== 'category';

        this.props.store.makeCopy(item)
            .then(() => snack.showSuccess(<Fragment>
                {name} <b>{item.name}</b> has been duplicated{isDisabled ? ' and is currently disabled' : ''}.
            </Fragment>))
            .catch(() => snack.showError(<Fragment>{name} <b>{item.name}</b> duplication failed.</Fragment>));
    };

    renderName = (item) => <span className={block('CellContent', { ellipsis: !item.isDefault })}>
        {this.props.renderName
            ? this.props.renderName(item)
            : <Fragment>
                {item.isDefault
                    ? <PfptLabelWithInfo
                        tooltip={this.props.defaultItemInfo}
                        tooltipText=""
                    >
                        {this.props.defaultItemName ?? 'Default ' + this.props.itemName}
                    </PfptLabelWithInfo>
                    : item.name
                }
            </Fragment>
        }
    </span>;

    isChangePriorityActionDisabled = () => {
        return !this.props.store.reorderedItems ||
            this.props.store.reorderedItems.filter((item) => !item.isDefault).length <= 1;
    };

    render() {
        const {
            store,
            searchPlaceholder,
            itemName,
            changePriorityAction,
            className,
            columnsConfig,
            priorityMode,
            searchFilter,
            noItemsText,
            subHeader,
            sortFn,
            readOnly,
            ...otherProps
        } = this.props;

        const colCfg = columnsConfig || {};
        colCfg.extraColumns = colCfg.extraColumns || [];
        colCfg.colFilter = colCfg.colFilter || (() => true);

        const cols = [
            ...this.columns.filter((column) =>
                (colCfg.colFilter(column) && (column.index !== PolicyTable.COL_INDEXES.MENU || !readOnly)) ||
                column.index === PolicyTable.COL_INDEXES.RAW), // Always keep the debug column
            ...colCfg.extraColumns
        ].sort((x, y) => (x.index > y.index ? 1 : -1));

        const searchString = store.search.getValue().toLowerCase();
        const searchFn = searchFilter ? searchFilter(store.search.getValue()) :
            ((arr) => arr.filter(({ id, name }) => (id + name).toLowerCase().includes(searchString)));

        let items = searchFn(store.items);
        if (sortFn && items.length > 0) {
            items = items.sort(sortFn);
        }

        let focusedItemId;
        if (store.focusItemIndex !== null) {
            focusedItemId = (store.reorderedItems[store.focusPriorityItemIndex] || {}).id;
        }

        return <Card className={cn(block(), className)}>
            {!priorityMode ? <Fragment>
                <Table
                    actions={readOnly ? null : [
                        ...(changePriorityAction ? [{
                            disabled: this.isChangePriorityActionDisabled(),
                            onPress: store.priorityChangeModal.on,
                            label: 'Change Priority',
                            loading: store.listStatus.isLoading,
                            qa: 'policy-priority',
                        }] : []),
                        {
                            icon: 'add',
                            qa: 'policy-create',
                            onPress: () => store.openModal(ModalMode.CREATE),
                            label: `New ${itemName}`,
                            loading: store.listStatus.isLoading,
                        }
                    ]}
                    subHeader={subHeader}
                    columns={cols}
                    cellClassName={block('Cell')}
                    focusedItemId={store.menu.id}
                    items={items}
                    search={store.search}
                    noItemsText={store.items.length === 0 && !readOnly ?
                        <div className={block('NoItems')}>Get started by
                            <FuseButton
                                className={block('NoItems__Cta')}
                                wrapperClassName={block('NoItems__Cta__Wrapper')}
                                color={'blue'}
                                type={'tertiary'}
                                qa="policy-create-link"
                                onClick={() => store.openModal(ModalMode.CREATE)}
                            >
                                {noItemsText}
                            </FuseButton>
                        </div> : null
                    }
                    onRowClick={(item) => store.openModal(ModalMode.VIEW, item)}
                    searchPlaceholder={searchPlaceholder}
                    loading={store.listStatus.isLoading && store.items.length === 0}
                    {...otherProps}
                />
            </Fragment> : <Fragment>
                <Table
                    rowsDraggable
                    subHeader={subHeader}
                    columns={this.priorityTableColumns}
                    onItemMove={(fromIndex, toIndex) => this.props.store.changeItemsOrder(fromIndex, toIndex)}
                    canItemMove={(item) => !item.isDefault}
                    cellClassName={block('PriorityTableCell')}
                    items={searchFn(store.reorderedItems)}
                    focusedItemId={focusedItemId}
                />
            </Fragment>}
        </Card>;
    }
}

export default inject('snack')(observer(PolicyTable));
